import { Check, Flame, Gift, Lock } from "lucide-react";
import { useTranslation } from "react-i18next";

import { cn } from "@/lib/utils";
import { useStreakMilestones, type StreakMilestone, type UserStreakMilestone } from "@/hooks/use-gamification";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { typeCaptionClass, typeMetaClass } from "@/lib/typography";

interface StreakMilestonesProps {
  currentStreak: number;
  onClaimMilestone?: (
    milestoneId: string,
    badgeName: string,
    xpBonus: number,
    freezesAwarded: number,
  ) => void;
  className?: string;
}

/**
 * The badge ladder — reached, claimable, and still ahead.
 *
 * ## One state carries the accent, not four
 *
 * Every row was picking its own fill from a five-way conditional
 * (`bg-amber-50`, `bg-primary/5`, `bg-card/50`, `bg-card`) with matching
 * borders, so the list had four competing colour states and the one that
 * mattered — *this is the next badge* — was the least saturated of them. Now
 * only "claimable" is tinted (amber, because amber is this product's "ready to
 * collect" signal everywhere else); reached rows are quiet and the next one is
 * marked by its progress bar and a ring.
 */
export const StreakMilestones = ({
  currentStreak,
  onClaimMilestone,
  className,
}: StreakMilestonesProps) => {
  const { t } = useTranslation();
  const { allMilestones, userMilestones, isLoading, claimMilestone, isClaiming } =
    useStreakMilestones();

  if (isLoading) {
    return (
      <div className={cn("space-y-2", className)} role="status" aria-busy="true">
        <span className="sr-only">{t("gamification.streak.loadingMilestones", "Loading milestones…")}</span>
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} className="h-16 w-full rounded-xl" />
        ))}
      </div>
    );
  }

  if (allMilestones.length === 0) return null;

  const achievedMap = new Map<string, UserStreakMilestone>();
  userMilestones.forEach((um) => achievedMap.set(um.milestone_id, um));

  const sortedMilestones = [...allMilestones].sort((a, b) => a.streak_days - b.streak_days);

  const nextMilestone = sortedMilestones.find((m) => m.streak_days > currentStreak);

  const handleClaim = async (milestone: StreakMilestone) => {
    try {
      const result = await claimMilestone(milestone.id);
      if (result?.success && onClaimMilestone) {
        onClaimMilestone(
          milestone.id,
          milestone.badge_name,
          milestone.xp_bonus,
          milestone.freezes_awarded,
        );
      }
    } catch {
      // Silent fail — the claim is idempotent server-side and the badge list
      // re-reads on the next render, so a failure here is not worth a toast.
    }
  };

  return (
    <div className={cn("space-y-2", className)}>
      {nextMilestone && (
        <p className={cn("flex items-center gap-1.5 pb-1", typeCaptionClass)}>
          <Flame className="h-3.5 w-3.5 shrink-0 text-secondary" aria-hidden="true" />
          {t("gamification.streak.daysToMilestone", {
            defaultValue: "{{count}} days to {{badge}}",
            count: Math.max(0, nextMilestone.streak_days - currentStreak),
            badge: nextMilestone.badge_name,
          })}
        </p>
      )}

      <ul className="space-y-2">
        {sortedMilestones.map((milestone) => {
          const achieved = achievedMap.get(milestone.id);
          const isClaimed = achieved?.is_claimed ?? false;
          const isAchieved =
            Boolean(achieved) || (!isClaimed && currentStreak >= milestone.streak_days);
          const isNext = milestone.id === nextMilestone?.id;
          const canClaim = isAchieved && !isClaimed;
          const progress = isAchieved
            ? 100
            : Math.min(100, (currentStreak / milestone.streak_days) * 100);

          return (
            <li
              key={milestone.id}
              className={cn(
                "flex items-center gap-3 rounded-xl border p-3 transition-colors",
                canClaim
                  ? "border-amber-200 bg-amber-50/70 dark:border-amber-900/60 dark:bg-amber-950/20"
                  : isNext
                    ? "border-stone-200 bg-white dark:border-stone-800 dark:bg-stone-900"
                    : "border-stone-200/70 bg-stone-50/70 dark:border-stone-800/70 dark:bg-stone-800/30",
                !isAchieved && "opacity-70",
              )}
            >
              <span
                className={cn(
                  "flex h-9 w-9 shrink-0 items-center justify-center rounded-full",
                  isClaimed
                    ? "bg-primary/10 text-primary"
                    : canClaim
                      ? "bg-amber-100 text-amber-700 dark:bg-amber-900/50 dark:text-amber-300"
                      : "bg-stone-200/70 text-stone-400 dark:bg-stone-700/70 dark:text-stone-500",
                )}
                aria-hidden="true"
              >
                {isClaimed ? (
                  <Check className="h-4 w-4" />
                ) : canClaim ? (
                  <Gift className="h-4 w-4" />
                ) : (
                  <Lock className="h-3.5 w-3.5" />
                )}
              </span>

              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="truncate text-sm font-semibold text-stone-900 dark:text-stone-50">
                    {milestone.badge_name}
                  </span>
                  {isNext && (
                    <span className={cn("shrink-0 rounded-full bg-stone-200/80 px-1.5 py-0.5", typeMetaClass)}>
                      {t("gamification.streak.next", "Next")}
                    </span>
                  )}
                </div>

                <div className="mt-1.5 flex items-center gap-2">
                  <div
                    role="progressbar"
                    aria-valuemin={0}
                    aria-valuemax={milestone.streak_days}
                    aria-valuenow={Math.min(currentStreak, milestone.streak_days)}
                    aria-label={milestone.badge_name}
                    className="h-1.5 min-w-0 flex-1 overflow-hidden rounded-full bg-stone-200 dark:bg-stone-800"
                  >
                    <div
                      className={cn(
                        "h-full rounded-full transition-[width] duration-500",
                        isAchieved ? "bg-primary" : "bg-secondary",
                      )}
                      style={{ width: `${progress}%` }}
                    />
                  </div>
                  <span className={cn("shrink-0 tabular-nums", typeCaptionClass)}>
                    {Math.min(currentStreak, milestone.streak_days)}/{milestone.streak_days}
                  </span>
                </div>

                {(milestone.xp_bonus > 0 || milestone.freezes_awarded > 0) && (
                  <p className={cn("mt-1 flex flex-wrap gap-x-2", typeCaptionClass)}>
                    {milestone.xp_bonus > 0 && (
                      <span className="font-semibold text-secondary">+{milestone.xp_bonus} XP</span>
                    )}
                    {milestone.freezes_awarded > 0 && (
                      <span className="font-semibold text-cyan-600 dark:text-cyan-400">
                        {/* Plural via the `freezes` key's count forms — the old
                            `{{count}} {{freezeWord}}` shape always resolved to
                            the singular, so "+2 freeze" was on screen. */}
                        +
                        {t("gamification.streak.freezes", {
                          defaultValue: "{{count}} freezes",
                          count: milestone.freezes_awarded,
                        })}
                      </span>
                    )}
                  </p>
                )}
              </div>

              {canClaim && (
                <Button
                  size="sm"
                  className="h-8 shrink-0 rounded-lg px-3 text-xs"
                  disabled={isClaiming}
                  onClick={() => handleClaim(milestone)}
                >
                  {t("common.actions.claim", "Claim")}
                </Button>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export default StreakMilestones;